/**
 * Donut share chart with hover: a ring of segments, the hovered segment
 * thickens and the centre shows its label + share; idle, the centre shows
 * the total. Inline SVG, no dependency (CLAUDE.md §8).
 */

import { useState } from 'react';

import { useC } from '../../lib/theme.js';

const MONO = "'JetBrains Mono', monospace";
const SANS = "'Geist', sans-serif";

export interface DonutSlice {
  readonly label: string;
  readonly value: number;
  readonly color: string;
}

export interface DonutProps {
  readonly slices: readonly DonutSlice[];
  readonly size?: number | undefined;
  readonly thickness?: number | undefined;
  readonly centerLabel?: string | undefined;
  readonly formatValue?: ((n: number) => string) | undefined;
  readonly 'aria-label'?: string | undefined;
}

function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

/** Default count formatter: thousands separators (3,455 not 3455). */
const formatCount = (n: number): string => n.toLocaleString('en-US');

export function Donut({ slices, size = 132, thickness = 14, centerLabel, formatValue = formatCount, 'aria-label': ariaLabel }: DonutProps): JSX.Element {
  const C = useC();
  const [hover, setHover] = useState<number | null>(null);
  const total = slices.reduce((s, x) => s + x.value, 0);
  const cx = size / 2, cy = size / 2, r = size / 2 - thickness;
  const gap = 0.03;
  let a = -Math.PI / 2;
  const hovered = hover !== null ? slices[hover] ?? null : null;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={ariaLabel} style={{ display: 'block', overflow: 'visible' }} onMouseLeave={() => setHover(null)} data-testid="donut">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={C.surface2} strokeWidth={thickness} />
      {total > 0 ? slices.map((s, i) => {
        if (s.value <= 0) return null;
        const span = (s.value / total) * Math.PI * 2;
        const a0 = a + (slices.length > 1 ? gap / 2 : 0);
        const a1 = a + span - (slices.length > 1 ? gap / 2 : 0);
        a += span;
        if (a1 <= a0) return null;
        const on = hover === i;
        return (
          <path
            key={s.label}
            d={arcPath(cx, cy, r, a0, a1)}
            fill="none"
            stroke={s.color}
            strokeWidth={on ? thickness + 4 : thickness}
            strokeLinecap="butt"
            opacity={hover === null || on ? 1 : 0.45}
            onMouseEnter={() => setHover(i)}
            onTouchStart={() => setHover(i)}
            style={{ transition: 'stroke-width 120ms, opacity 120ms', cursor: 'default' }}
          />
        );
      }) : null}
      <text x={cx} y={cy - 4} textAnchor="middle" fontFamily={MONO} fontSize={hovered !== null ? 15 : 17} fontWeight={600} fill={C.text}>
        {hovered !== null ? `${total > 0 ? Math.round((hovered.value / total) * 100) : 0}%` : formatValue(total)}
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontFamily={hovered !== null ? SANS : MONO} fontSize={hovered !== null ? 10 : 8} letterSpacing={hovered !== null ? 0 : 1.5} fill={C.muted}>
        {(hovered !== null ? `${hovered.label} · ${formatValue(hovered.value)}` : (centerLabel ?? 'TOTAL')).slice(0, 26)}
      </text>
    </svg>
  );
}

/** A single 100 % bar split into segments, with hover. */
export function SegmentBar({ slices, height = 12, formatValue = formatCount }: { slices: readonly DonutSlice[]; height?: number; formatValue?: (n: number) => string }): JSX.Element {
  const C = useC();
  const [hover, setHover] = useState<number | null>(null);
  const total = slices.reduce((s, x) => s + x.value, 0);
  const hovered = hover !== null ? slices[hover] ?? null : null;
  return (
    <div onMouseLeave={() => setHover(null)} data-testid="segment-bar">
      <div style={{ display: 'flex', height, borderRadius: 2, overflow: 'hidden', background: C.surface2, gap: 2 }}>
        {slices.map((s, i) => (s.value > 0 && total > 0 ? (
          <div
            key={s.label}
            onMouseEnter={() => setHover(i)}
            onTouchStart={() => setHover(i)}
            style={{ width: `${(s.value / total) * 100}%`, background: s.color, opacity: hover === null || hover === i ? 1 : 0.4, transition: 'opacity 120ms', minWidth: 3 }}
          />
        ) : null))}
      </div>
      <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: 1, color: hovered === null ? C.dim : C.text, marginTop: 6, minHeight: 14 }}>
        {hovered !== null ? `${hovered.label.toUpperCase()} · ${formatValue(hovered.value)} · ${Math.round((hovered.value / Math.max(1, total)) * 100)}%` : 'HOVER A SEGMENT'}
      </div>
    </div>
  );
}
